import { messaging, getToken, deleteToken } from '../firebaseConfig'
import { API_BASE_URL } from './apiConfig'

const VAPID_KEY = 'BH-9vai85D4I8g0DmH5DdKQs-dNI9Z7PgPsow5NEVb8B24OT3flXfY2LAOITtWEJzXDXW_emTBlnySu8rvtPfpk'

let currentToken = null

// The service worker can't read the app's config, so pass it the backend API address in its URL
// (it needs it for the OK/Stop buttons on notifications)
const SERVICE_WORKER_URL = `/firebase-messaging-sw.js?api=${encodeURIComponent(API_BASE_URL)}`

export function registerServiceWorker() {
  return navigator.serviceWorker.register(SERVICE_WORKER_URL, { updateViaCache: 'none' })
}

export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    console.log('This browser does not support desktop notification')
    return false
  }

  if (Notification.permission === 'granted') {
    return true
  }

  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission()
    return permission === 'granted'
  }

  return false
}

export async function getFCMToken() {
  try {
    if (!messaging) {
      console.warn('Firebase messaging is not initialized - notifications unavailable')
      return null
    }

    let swRegistration = null
    if ('serviceWorker' in navigator) {
      try {
        swRegistration = await registerServiceWorker()
        console.log('Service Worker registered successfully for push:', swRegistration.scope)
      } catch (swErr) {
        console.warn('Service Worker registration failed:', swErr)
      }
    }

    currentToken = await getToken(messaging, { 
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swRegistration || undefined
    })
    
    if (currentToken) {
      console.log('FCM token obtained:', currentToken)
      return currentToken
    } else {
      console.log('No registration token available')
      return null
    }
  } catch (error) {
    console.warn('Error getting FCM token:', error)
    return null
  }
}

// The service worker shows every push in the notification panel and forwards it here,
// so open pages can refresh. Returns an unsubscribe function.
export function onMessageReceived(callback) {
  if (!('serviceWorker' in navigator)) return () => {}

  const handler = (event) => {
    if (event.data?.type !== 'PUSH') return
    console.log('Push received:', event.data.payload)
    callback(event.data.payload)
  }
  navigator.serviceWorker.addEventListener('message', handler)
  return () => navigator.serviceWorker.removeEventListener('message', handler)
}

export function getCurrentToken() {
  return currentToken
}

export async function registerInstallation(userId, userType = 'employee', visitorId = null) {
  try {
    const token = await getFCMToken()
    if (!token) return null

    const response = await fetch(`${API_BASE_URL}/notifications/installations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token,
        userId,
        userType,
        visitorId
      })
    })

    if (!response.ok) {
      throw new Error('Failed to register installation')
    }

    return await response.json()
  } catch (error) {
    console.warn('Error registering installation:', error)
    return null
  }
}

// Stop this device getting notifications for the user who is logging out
export async function unregisterCurrentDevice() {
  try {
    if (!('serviceWorker' in navigator)) return
    const registration = await navigator.serviceWorker.getRegistration()
    if (!registration) return

    // Clear any alerts still showing for the previous user
    const shown = await registration.getNotifications()
    shown.forEach(notification => notification.close())

    if (!messaging || !('Notification' in window) || Notification.permission !== 'granted') return
    const token = currentToken || await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration })
    if (token) await unregisterInstallation(token)
    await deleteToken(messaging)
    currentToken = null
  } catch (error) {
    console.warn('Error unregistering device from notifications:', error)
  }
}

export async function unregisterInstallation(token) {
  try {
    await fetch(`${API_BASE_URL}/notifications/installations`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token })
    })
  } catch (error) {
    console.warn('Error unregistering installation:', error)
  }
}
